import { filter, takeUntil } from 'rxjs/operators';
import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { IAnimatedButtonSettings } from '@shared/components/animated-button/animated-button-settings';
import { AnimatedButtonState } from '@shared/components/animated-button/animated-button-state';
import { IErrorInfo } from '@shared/components/modal-form-host/error-info';
import { ModalActionStatus } from '@shared/components/modal-form-host/modal-action-status';
import { ModalButtons } from '@shared/components/modal-form-host/modal-buttons';
import { FormHostState, ModalFormHostStateService } from '@shared/components/modal-form-host/modal-form-host-state.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { StyleService } from '@shared/services/style/style.service';

@Component({
    selector: 'app-components-modal-form-host',
    templateUrl: './modal-form-host.component.html',
    styleUrls: ['./modal-form-host.component.scss']
})
export class ModalFormHostComponent implements OnInit, OnDestroy, OnChanges {
    constructor(
        public state: ModalFormHostStateService,
        public styleService: StyleService
    ) {}

    private destroy$ = new ReplaySubject<boolean>();
    public modalButtons = ModalButtons;
    public saveButtonState = AnimatedButtonState.Default;
    private modalActionStatusSubscription: Subscription;

    @Input()
    get name(): string { return this.name$.value; }
    set name(value: string) { this.name$.next(value); }
    public name$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

    @Input()
    get modal(): BsModalRef { return this.modal$.value; }
    set modal(value: BsModalRef) { this.modal$.next(value); }
    public modal$: BehaviorSubject<BsModalRef> = new BehaviorSubject<BsModalRef>(undefined);

    @Input()
    get title(): string { return this.title$.value; }
    set title(value: string) { this.title$.next(value); }
    public title$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

    @Input()
    get icon(): string { return this.icon$.value; }
    set icon(value: string) { this.icon$.next(value); }
    public icon$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

    @Input()
    get formId(): string { return this.formId$.value; }
    set formId(value: string) { this.formId$.next(value); }
    public formId$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

    @Input()
    get status(): ModalActionStatus { return this.status$.value; }
    set status(value: ModalActionStatus) { this.status$.next(value); }
    public status$ = new BehaviorSubject<ModalActionStatus>(ModalActionStatus.Iddle);

    @Input()
    get error(): IErrorInfo { return this.error$.value; }
    set error(value: IErrorInfo) { this.error$.next(value); }
    public error$ = new BehaviorSubject<IErrorInfo>(null);

    @Input()
    get errorAmount(): number { return this.errorAmount$.value; }
    set errorAmount(value: number) { this.errorAmount$.next(value); }
    public errorAmount$ = new BehaviorSubject<number>(0);

    @Input()
    get buttons(): number { return this.buttons$.value; }
    set buttons(value: number) { this.buttons$.next(value); }
    public buttons$ = new BehaviorSubject<number>(ModalButtons.Save);

    @Input()
    get hasFooter(): boolean { return this.hasFooter$.value; }
    set hasFooter(value: boolean) { this.hasFooter$.next(value); }
    public hasFooter$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Input()
    get isDisabled(): boolean { return this.isDisabled$.value; }
    set isDisabled(value: boolean) { this.isDisabled$.next(value); }
    public isDisabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Input()
    get successText(): string { return this.successText$.value; }
    set successText(value: string) { this.successText$.next(value); }
    public successText$ = new BehaviorSubject<string>('SHARED.COMPONENTS.BUTTON.SAVED');

    @Input()
    get validateForm(): boolean { return this.validateForm$.value; }
    set validateForm(value: boolean) { this.validateForm$.next(value); }
    public validateForm$ = new BehaviorSubject<boolean>(true);

    @Input()
    get preventUnloadWarning(): boolean { return this.preventUnloadWarning$.value; }
    set preventUnloadWarning(value: boolean) { this.preventUnloadWarning$.next(value); }
    public preventUnloadWarning$ = new BehaviorSubject<boolean>(false);

    @Input()
    public modalActionStatus$: Observable<ModalActionStatus>;

    public modalOptions: Partial<IAnimatedButtonSettings> = {
        returnToDefaultState: false,
        enabledOnCompleted: false
    };

    public modalOption: Partial<IAnimatedButtonSettings> = {
        // returnToDefaultState: false
        enabledOnCompleted: false
    };

    public onAnimatedButtonChanged(state) {
        this.state.animatedButtonState = state;
    }

    public onSubmit(data: any) {
        const form = !!this.name && !!this.state.states[this.name] && !!this.state.states[this.name].form ? this.state.states[this.name].form : this.state.form;

        if (this.validateForm) {
            form.form.updateValueAndValidity();
            const formControls = form.form.controls;
            for (const key in formControls) {
                if (form.controls.hasOwnProperty(key)) {
                    this.validateControl(formControls[key]);
                }
            }

            if (!form.valid) {
                return;
            }
        }

        this.saveButtonState = AnimatedButtonState.Submitting;
        form.onSubmit(null);
    }

    private validateControl(control) {
        if (control.invalid) {
            if (control['controls']) {
                if (control instanceof UntypedFormArray) {
                    control['controls'].forEach(el => {
                        if (el['controls']) {
                            this.validateControl(el);
                        } else {
                            el.markAsTouched();
                        }
                    });
                } else if (control instanceof UntypedFormGroup) {
                    for (const key in control['controls']) {
                        if (control['controls'][key].invalid) {
                            if (control['controls'][key]['controls']) {
                                this.validateControl(control['controls'][key]);
                            } else {
                                control['controls'][key].markAsTouched();
                            }
                        }
                    }
                }
            } else {
                control.markAsTouched();
            }
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    public unloadNotification($event: any) {
        // Allow parent component to disable the warning (e.g., during logout)
        if (this.preventUnloadWarning) {
            return;
        }
        $event.returnValue = true;
    }

    public ngOnInit(): void {

        if (!!this.name) {
            this.state.states[this.name] = new FormHostState(this.name);
        } else {
            this.modal$.pipe(
                takeUntil(this.destroy$),
                filter(x => !!x),)
                .subscribe(x => {
                    this.status = ModalActionStatus.Iddle;
                    this.error = null;
                });

            this.status$.pipe(
                takeUntil(this.destroy$))
                .subscribe(x => {
                    this.state.status = x;
                });

            this.status$.pipe(
                takeUntil(this.destroy$),
                filter(x => x === ModalActionStatus.Finished),)
                .subscribe(x => this.saveButtonState = AnimatedButtonState.Success);

            this.status$.pipe(
                takeUntil(this.destroy$),
                filter(x => x === ModalActionStatus.Iddle),)
                .subscribe(x => this.saveButtonState = AnimatedButtonState.Default);

            this.status$.pipe(
                takeUntil(this.destroy$),
                filter(x => x === ModalActionStatus.Error),)
                .subscribe(x => this.saveButtonState = AnimatedButtonState.Error);

            this.error$.pipe(
                takeUntil(this.destroy$))
                .subscribe(x => this.state.error = x);
        }
    }

    public ngOnDestroy(): void {
        if (!!this.name) {
            this.state.states[this.name].clear();
        } else {
            this.state.clear();
        }

        this.destroy$.next(true);
        this.destroy$.complete();
    }

    public onCancel() {
        this.modal.hide();
        if (!!this.name) {
            this.state.states[this.name].clear();
        } else {
            this.state.clear();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.modalActionStatus$ !== undefined) {
            this.modalActionStatusSubscription = this.modalActionStatus$.pipe(
                takeUntil(this.destroy$)).subscribe(status => {
                    this.status = status;
                });
        } else {
            this.modalActionStatusSubscription?.unsubscribe();
        }
    }
}
